import React, { useState, useEffect } from 'react';
import { getGames } from '../../lib/bettingApi';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import CreateGame from './CreateGame';
import './GamesList.css';

// Helper to darken a hex color by a given factor (0–1, where 0.4 = darken by 40%)
const darkenHexColor = (hex, darkenFactor = 0.4) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || (hex.length !== 7 && hex.length !== 4)) {
    return hex || '#000000';
  }

  // Expand shorthand form (#abc) to full form (#aabbcc)
  let fullHex = hex;
  if (hex.length === 4) {
    fullHex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }

  const r = parseInt(fullHex.slice(1, 3), 16);
  const g = parseInt(fullHex.slice(3, 5), 16);
  const b = parseInt(fullHex.slice(5, 7), 16);

  const factor = Math.max(0, Math.min(1, 1 - darkenFactor)); // e.g. 0.6 for 40% darken

  const dr = Math.round(r * factor);
  const dg = Math.round(g * factor);
  const db = Math.round(b * factor);

  return `rgb(${dr}, ${dg}, ${db})`;
};

// Helper to convert hex color to rgba with opacity
const hexToRgba = (hex, opacity = 0.4) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return hex || 'rgba(0, 0, 0, 0.4)';
  }

  // Expand shorthand form (#abc) to full form (#aabbcc)
  let fullHex = hex;
  if (hex.length === 4) {
    fullHex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }

  const r = parseInt(fullHex.slice(1, 3), 16);
  const g = parseInt(fullHex.slice(3, 5), 16);
  const b = parseInt(fullHex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Timer component that updates in real-time
function GameTimer({ endsAt }) {
  const [timeText, setTimeText] = useState('');

  useEffect(() => {
    if (!endsAt) return;

    const calculateTime = () => {
      const now = new Date();
      const endDate = new Date(endsAt);
      const diff = endDate - now;

      if (diff <= 0) {
        setTimeText('Expired');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      let text = '';
      if (days > 0) {
        text = `${days} ${days === 1 ? 'day' : 'days'} left`;
      } else if (hours > 0) {
        text = `${hours} ${hours === 1 ? 'hour' : 'hours'} left`;
      } else if (minutes > 0) {
        text = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} left`;
      } else {
        text = 'Less than a minute left';
      }

      setTimeText(text);
    };

    // Calculate immediately
    calculateTime();

    // Update every second
    const interval = setInterval(calculateTime, 1000);

    return () => clearInterval(interval);
  }, [endsAt]);

  if (!timeText) return null;

  return (
    <div className="game-card-timer">
      <img src="/images/clock.png" alt="Clock" className="game-card-clock-icon" />
      <span className="game-card-time-text">{timeText}</span>
    </div>
  );
}

function GamesList({ onSelectGame, showCreateGame, setShowCreateGame, searchQuery = '', activeTab = 'explore', setActiveTab, onLoginRequired }) {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [containerPaddingTop, setContainerPaddingTop] = useState(100);
  const [creatorNames, setCreatorNames] = useState({}); // Map of gameId -> creator nickname

  useEffect(() => {
    fetchGames();
    
    // Subscribe to real-time updates
    const subscription = subscribeToGames();
    
    // Cleanup subscription on unmount
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Calculate container padding based on header
  // Game windows should be 30px from main menu
  // Since tabs are fixed, they don't take up space in the flow
  useEffect(() => {
    const updatePadding = () => {
      const header = document.querySelector('.user-header');
      
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const headerBottom = headerRect.bottom;
        const gapFromMenu = 58; // 58px from main menu to game windows
        
        // Calculate: header bottom + gap from menu
        setContainerPaddingTop(headerBottom + gapFromMenu);
      }
    };

    // Initial calculation
    updatePadding();

    // Update on resize
    window.addEventListener('resize', updatePadding);
    
    // Use ResizeObserver to watch for header and tabs size changes
    const header = document.querySelector('.user-header');
    const tabsContainer = document.querySelector('.games-tabs-container');
    let headerResizeObserver, tabsResizeObserver;
    
    if (header && window.ResizeObserver) {
      headerResizeObserver = new ResizeObserver(updatePadding);
      headerResizeObserver.observe(header);
    }
    
    if (tabsContainer && window.ResizeObserver) {
      tabsResizeObserver = new ResizeObserver(updatePadding);
      tabsResizeObserver.observe(tabsContainer);
    }

    return () => {
      window.removeEventListener('resize', updatePadding);
      if (headerResizeObserver) {
        headerResizeObserver.disconnect();
      }
      if (tabsResizeObserver) {
        tabsResizeObserver.disconnect();
      }
    };
  }, []);

  const handleGameCreated = (newGame) => {
    // Refresh games list
    fetchGames();
    setShowCreateGame(false);
  };

  const fetchGames = async () => {
    try {
      setLoading(true);
      // Fetch both active and ended votes (ended votes are kept for 7 days for display)
      // This works for both logged-in and non-logged-in users (public access)
      const data = await getGames('all');
      let gamesList = data.games || [];
      
      // Filter to show active votes and recently ended votes (within last 7 days)
      // Expired votes are visible to all users, including non-logged-in users
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      gamesList = gamesList.filter(game => {
        if (game.status === 'active') return true;
        if (game.status === 'ended') {
          // Only show ended votes from the last 7 days
          const endsAt = game.ends_at ? new Date(game.ends_at) : null;
          return endsAt && endsAt >= sevenDaysAgo;
        }
        return false;
      });
      
      setGames(gamesList);
      setError('');
      
      // Fetch creator profiles for all games
      fetchCreatorProfiles(gamesList);
      
      // Check for expired votes and process them automatically
      // This is a fallback since Vercel Hobby plan doesn't support frequent cron jobs
      const now = new Date();
      const expiredGames = gamesList.filter(game => {
        if (!game.ends_at || game.status !== 'active') return false;
        const endsAt = new Date(game.ends_at);
        return endsAt <= now;
      });
      
      // Process expired votes in the background (don't block UI)
      if (expiredGames.length > 0) {
        console.log(`Found ${expiredGames.length} expired vote(s), processing...`);
        processExpiredVotes().catch(err => {
          console.error('Error processing expired votes:', err);
          // Don't show error to user, just log it
        });
      }
    } catch (err) {
      console.error('Error fetching games:', err);
      setError('Failed to load games. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreatorProfiles = async (gamesList) => {
    try {
      // Get unique creator IDs (filter out null/undefined)
      const creatorIds = [...new Set(gamesList.map(game => game.created_by).filter(id => id))];
      
      if (creatorIds.length === 0) {
        return;
      }

      // Fetch user profiles for creators
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles_public')
        .select('id, nickname')
        .in('id', creatorIds);

      if (profilesError) {
        console.error('Error fetching creator profiles:', profilesError);
        return;
      }

      // Create a map of creator_id to nickname
      const creatorMap = {};
      if (profiles && profiles.length > 0) {
        profiles.forEach(profile => {
          if (profile && profile.id && profile.nickname) {
            creatorMap[profile.id] = profile.nickname;
          }
        });
      }

      // Create a map of gameId -> creator nickname
      const gameCreatorMap = {};
      gamesList.forEach(game => {
        if (game.created_by && creatorMap[game.created_by]) {
          gameCreatorMap[game.id] = creatorMap[game.created_by];
        }
      });

      setCreatorNames(gameCreatorMap);
    } catch (err) {
      console.error('Error fetching creator profiles:', err);
    }
  };

  // Function to process expired votes
  // Call the database function directly to avoid CORS issues
  const processExpiredVotes = async () => {
    try {
      // Call the RPC function directly using Supabase client
      // This avoids CORS issues and works from the client
      const { data, error } = await supabase.rpc('process_expired_votes_with_payouts');

      if (error) {
        console.error('Error processing expired votes:', error);
        return;
      }

      const result = data?.[0] || {};
      console.log('Processed expired votes:', result);
      
      // Refresh games list after processing
      setTimeout(() => {
        fetchGames();
      }, 1000);
    } catch (err) {
      console.error('Error calling process function:', err);
      // Don't throw - this is background processing, don't break the UI
    }
  };

  const subscribeToGames = () => {
    // Subscribe to real-time updates for games table
    // Listen to all status changes (active and ended)
    const channel = supabase
      .channel('games-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'games'
        },
        (payload) => {
          console.log('Game update:', payload);
          // Refresh games list when any game is updated
          // This includes status changes from active to ended
          fetchGames();
        }
      )
      .subscribe();

    return channel;
  };


  if (loading) {
    return (
      <div className="games-list-container" style={{ paddingTop: `${containerPaddingTop}px` }}>
        <div className="games-loading">Loading games...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="games-list-container" style={{ paddingTop: `${containerPaddingTop}px` }}>
        <div className="games-error">{error}</div>
        <button onClick={fetchGames} className="retry-btn">Retry</button>
      </div>
    );
  }

  // Check if a game is expired (ends_at < now) or has status 'ended'
  const isGameExpired = (game) => {
    if (game.status === 'ended') return true;
    if (game.ends_at) {
      const now = new Date();
      const endsAt = new Date(game.ends_at);
      return endsAt <= now;
    }
    return false;
  };

  // Filter games based on active tab and search query
  // This works for both logged-in and non-logged-in users
  const filteredGames = games.filter((game) => {
    // First filter by tab
    if (activeTab === 'explore') {
      // Show only active games
      if (isGameExpired(game)) return false;
      if (game.status !== 'active') return false;
    } else if (activeTab === 'expired') {
      // Show only ended games - visible to all users including non-logged-in
      if (!isGameExpired(game)) return false;
    } else if (activeTab === 'watchlist') {
      // Empty for now
      return false;
    }
    
    // Then filter by search query
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    return game.title?.toLowerCase().includes(query) ||
           game.description?.toLowerCase().includes(query);
  });

  if (games.length === 0) {
    return (
      <div className="games-list-container" style={{ paddingTop: `${containerPaddingTop}px` }}>

        {showCreateGame && (
          <div className="create-game-overlay">
            <div className="create-game-modal">
              <CreateGame
                onGameCreated={handleGameCreated}
                onClose={() => setShowCreateGame(false)}
                onLoginRequired={onLoginRequired}
              />
            </div>
          </div>
        )}

        <div className="games-empty">
          {activeTab === 'explore' && (
            <>
              <p>No active games at the moment.</p>
              <p>Check back soon or create a new game!</p>
            </>
          )}
          {activeTab === 'watchlist' && (
            <>
              <p>Your watchlist is empty.</p>
              <p>Add games to your watchlist to track them here!</p>
            </>
          )}
          {activeTab === 'expired' && (
            <>
              <p>No expired games to display.</p>
              <p>Expired games will appear here after they end.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (filteredGames.length === 0 && searchQuery.trim()) {
    return (
      <div className="games-list-container" style={{ paddingTop: `${containerPaddingTop}px` }}>

        {showCreateGame && (
          <div className="create-game-overlay">
            <div className="create-game-modal">
              <CreateGame
                onGameCreated={handleGameCreated}
                onClose={() => setShowCreateGame(false)}
                onLoginRequired={onLoginRequired}
              />
            </div>
          </div>
        )}

        <div className="games-empty">
          <p>No games found matching "{searchQuery}".</p>
          <p>Try a different search term{activeTab === 'explore' ? ' or create a new game' : ''}!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="games-list-container" style={{ paddingTop: `${containerPaddingTop}px` }}>

      {showCreateGame && (
        <div className="create-game-overlay">
          <div className="create-game-modal">
            <CreateGame
              onGameCreated={handleGameCreated}
              onClose={() => setShowCreateGame(false)}
              onLoginRequired={onLoginRequired}
            />
          </div>
        </div>
      )}

      {filteredGames.length === 0 && !searchQuery.trim() && (
        <div className="games-empty">
          {activeTab === 'explore' && (
            <>
              <p>No active games at the moment.</p>
              <p>Check back soon or create a new game!</p>
            </>
          )}
          {activeTab === 'watchlist' && (
            <>
              <p>Your watchlist is empty.</p>
              <p>Add games to your watchlist to track them here!</p>
            </>
          )}
          {activeTab === 'expired' && (
            <>
              <p>No expired games to display.</p>
              <p>Expired games will appear here after they end.</p>
            </>
          )}
        </div>
      )}

      {filteredGames.length > 0 && (
        <div className="games-grid">
          {filteredGames.map((game) => (
          <div
            key={game.id}
            className="game-card"
            onClick={() => onSelectGame && onSelectGame(game)}
          >
            {(() => {
              // Check if this is a multi-team game (>2 teams)
              const isMultiTeamGame = Array.isArray(game.teams) && game.teams.length > 2;
              
              // If multi-team, render simplified layout
              if (isMultiTeamGame) {
                // Calculate total pool for percentage calculation
                const totalPool = game.teams.reduce((sum, team) => sum + (parseFloat(team.pool) || 0), 0);
                
                return (
                  <>
                    <div className="game-card-header">
                      <div className="game-title-wrapper">
                        {game.title_image && (
                          <img
                            src={game.title_image}
                            alt={game.title}
                            className="game-title-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <h3 className="game-title">{game.title}</h3>
                      </div>
                      {isGameExpired(game) && (
                        <span className="game-status-badge ended">Ended</span>
                      )}
                    </div>

                    <div className="game-pools">
                      <div className="game-card-multi-team-list">
                        {game.teams.map((team, index) => {
                          const percent = totalPool > 0 ? ((parseFloat(team.pool) || 0) / totalPool) * 100 : 0;
                          const coefficient = team.coefficient || 1;
                          const teamColor = team.color || '#5428FF'; // Default to purple if no color
                          
                          return (
                            <div key={team.team_number || index} className="game-card-multi-team-row">
                              {/* Team name column with background bar */}
                              <div className="game-card-multi-team-name-container">
                                {/* Background percentage bar behind team name */}
                                <div
                                  className="game-card-multi-team-bar"
                                  style={{
                                    width: `${Math.max(0, Math.min(100, percent))}%`,
                                    backgroundColor: teamColor
                                  }}
                                />
                                <div className="game-card-multi-team-name">
                                  {team.name || `Team ${team.team_number || index + 1}`}
                                </div>
                              </div>
                              <div className="game-card-multi-team-percent">
                                {percent.toFixed(0)}%
                              </div>
                              <button
                                className={`game-card-multi-team-pick-btn ${isGameExpired(game) ? 'disabled' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isGameExpired(game)) {
                                    onSelectGame && onSelectGame(game);
                                  }
                                }}
                                disabled={isGameExpired(game)}
                                style={{ backgroundColor: '#5428FF' }}
                              >
                                Vote
                              </button>
                            </div>
                          );
                        })}
                      </div>

                      {/* Footer with Vol., Clock, Time on left and Mark on right - Only show for Explore tab (active games) */}
                      {activeTab === 'explore' && !isGameExpired(game) && (
                        <div className="game-card-footer">
                          <div className="game-card-footer-left">
                            <span className="game-card-volume">
                              ${(() => {
                                const totalVolume = game.teams.reduce((sum, team) => sum + (parseFloat(team.pool) || 0), 0);
                                return Math.floor(totalVolume).toLocaleString();
                              })()} Vol.
                            </span>
                            {game.status === 'active' && game.ends_at && (
                              <GameTimer endsAt={game.ends_at} />
                            )}
                            {/* Creator removed from card footer */}
                          </div>
                          <div className="game-card-footer-right">
                            <img 
                              src="/images/mark.png" 
                              alt="Mark" 
                              className="game-card-mark-icon"
                              onMouseEnter={(e) => e.target.src = "/images/markfill.png"}
                              onMouseLeave={(e) => e.target.src = "/images/mark.png"}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onLoginRequired) {
                                  onLoginRequired();
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              }
              
              // Original 2-team layout
              const coefYes = typeof game.coefficient_yes === 'number' ? game.coefficient_yes : 1;
              const coefNo = typeof game.coefficient_no === 'number' ? game.coefficient_no : 1;
              const totalCoef = coefYes + coefNo;
              // Reverse-mapped percentages: Yes gets No's share, No gets Yes's share
              const yesPercent = totalCoef > 0 ? (coefNo / totalCoef) * 100 : 50;
              const noPercent = totalCoef > 0 ? (coefYes / totalCoef) * 100 : 50;
              // Get team colors from game data, fallback to defaults
              const teamYesColor = game.team_yes_color || '#2551AF';
              const teamNoColor = game.team_no_color || '#E23838';

              return (
                <>
                  <div className="game-card-header">
                    <div className="game-title-wrapper">
                      {game.title_image && (
                        <img
                          src={game.title_image}
                          alt={game.title}
                          className="game-title-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <h3 className="game-title">{game.title}</h3>
                    </div>
                    {isGameExpired(game) && (
                      <span className="game-status-badge ended">Ended</span>
                    )}
                  </div>

                  <div className="game-pools">
                    <div className="coeff-vertical-container">
                      <div className="coeff-vertical">
                        <div className="coeff-square-wrapper">
                          {game.team_yes_image ? (
                            <img 
                              src={game.team_yes_image} 
                              alt={game.team_yes_name || 'Yes'} 
                              className="team-image team-image-yes"
                              onError={(e) => { 
                                e.target.style.display = 'none';
                                const colorSquare = e.target.nextElementSibling;
                                if (colorSquare) colorSquare.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <div 
                            className="coeff-color-square coeff-color-square-yes" 
                            style={{ 
                              display: game.team_yes_image ? 'none' : 'block',
                              backgroundColor: teamYesColor
                            }}
                          ></div>
                        </div>
                        <div className="coeff-bar-wrapper">
                          <div className="coeff-bar-vertical-wrapper">
                            <span 
                              className="coeff-percent-text coeff-percent-yes"
                            >
                              {yesPercent.toFixed(0)}%
                            </span>
                            <div
                              className="coeff-bar-vertical coeff-bar-yes"
                              style={{ 
                                height: `${yesPercent}%`,
                                backgroundColor: hexToRgba(teamYesColor, 0.4)
                              }}
                            >
                            </div>
                          </div>
                        </div>
                        <div className="coeff-bar-wrapper">
                          <div className="coeff-bar-vertical-wrapper">
                            <span 
                              className="coeff-percent-text coeff-percent-no"
                            >
                              {noPercent.toFixed(0)}%
                            </span>
                            <div
                              className="coeff-bar-vertical coeff-bar-no"
                              style={{ 
                                height: `${noPercent}%`,
                                backgroundColor: hexToRgba(teamNoColor, 0.4)
                              }}
                            >
                            </div>
                          </div>
                        </div>
                        <div className="coeff-square-wrapper">
                          {game.team_no_image ? (
                            <img 
                              src={game.team_no_image} 
                              alt={game.team_no_name || 'No'} 
                              className="team-image team-image-no"
                              onError={(e) => { 
                                e.target.style.display = 'none';
                                const colorSquare = e.target.nextElementSibling;
                                if (colorSquare) colorSquare.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <div 
                            className="coeff-color-square coeff-color-square-no" 
                            style={{ 
                              display: game.team_no_image ? 'none' : 'block',
                              backgroundColor: teamNoColor
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bet-buttons-container">
                      <button 
                        className={`bet-btn bet-btn-yes ${isGameExpired(game) ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isGameExpired(game)) {
                            onSelectGame && onSelectGame(game);
                          }
                        }}
                        disabled={isGameExpired(game)}
                        style={{ backgroundColor: hexToRgba(teamYesColor, 0.4) }}
                      >
                        {game.team_yes_name || 'YES'}
                      </button>
                      <button 
                        className={`bet-btn bet-btn-no ${isGameExpired(game) ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isGameExpired(game)) {
                            onSelectGame && onSelectGame(game);
                          }
                        }}
                        disabled={isGameExpired(game)}
                        style={{ backgroundColor: hexToRgba(teamNoColor, 0.4) }}
                      >
                        {game.team_no_name || 'NO'}
                      </button>
                    </div>

                    {/* Winner Display for Ended Votes - Below team buttons */}
                    {game.status === 'ended' && game.winner_side && (
                      <div className="game-card-winner">
                        <div className="game-card-winner-banner">
                          <span className="game-card-winner-label">Winner:</span>
                          <span className={`game-card-winner-team ${game.winner_side === 'yes' ? 'winner-yes' : 'winner-no'}`}>
                            {game.winner_side === 'yes' 
                              ? (game.team_yes_name || 'Yes')
                              : (game.team_no_name || 'No')
                            }
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Footer with Vol., Clock, Time on left and Mark on right - Only show for Explore tab (active games) */}
                    {activeTab === 'explore' && !isGameExpired(game) && (
                      <div className="game-card-footer">
                        <div className="game-card-footer-left">
                          <span className="game-card-volume">
                            ${(() => {
                              const poolYes = parseFloat(game.pool_yes) || 0;
                              const poolNo = parseFloat(game.pool_no) || 0;
                              const totalVolume = poolYes + poolNo;
                              return Math.floor(totalVolume).toLocaleString();
                            })()} Vol.
                          </span>
                          {game.status === 'active' && game.ends_at && (
                            <GameTimer endsAt={game.ends_at} />
                          )}
                          {/* Creator removed from card footer */}
                        </div>
                        <div className="game-card-footer-right">
                          <img 
                            src="/images/mark.png" 
                            alt="Mark" 
                            className="game-card-mark-icon"
                            onMouseEnter={(e) => e.target.src = "/images/markfill.png"}
                            onMouseLeave={(e) => e.target.src = "/images/mark.png"}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onLoginRequired) {
                                onLoginRequired();
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        ))}
        </div>
      )}
    </div>
  );
}

export default GamesList;

